import cx from 'classnames'
import { useController, useFormContext } from 'react-hook-form'

import { isFieldHighlighted } from '@helpers'

import { Input, InputLabel } from '../InputElements'
import { RecommendationSelect } from './elements'
import { FormControl } from './FormControl'
import { FormHelperText } from './FormHelperText'
import type { ReactHookFormTextFieldProps } from './ReactHookFormTextField'

type ReactHookFormRecommendationTextFieldProps = ReactHookFormTextFieldProps & {
  recommendations: string[] | undefined
}

export function ReactHookFormRecommendationTextField({
  className,
  disabled = false,
  endAdornment,
  highlighted = false,
  isLabelHighlighted = false,
  label,
  name,
  placeholder,
  recommendations = [],
  required = false,
  transform,
  type,
  ...rest
}: ReactHookFormRecommendationTextFieldProps) {
  const { control } = useFormContext()
  const {
    field,
    fieldState: { error },
    formState: { isSubmitting },
  } = useController({ control, name })

  const hasError = !!error
  const isFieldDisabled = disabled || isSubmitting

  return (
    <FormControl error={hasError}>
      {label && (
        <InputLabel error={hasError} required={required} highlighted={isLabelHighlighted} htmlFor={name}>
          {label}
        </InputLabel>
      )}
      {recommendations.length > 0 ? (
        <RecommendationSelect
          id={field.name}
          recommendations={recommendations}
          field={field}
          className={cx({ error: hasError })}
          disabled={isFieldDisabled}
          transform={transform}
        />
      ) : (
        <Input
          aria-describedby={error ? `${field.name}-helper-text` : undefined}
          aria-invalid={Boolean(error)}
          disabled={isFieldDisabled}
          error={Boolean(error)}
          highlighted={isFieldHighlighted(highlighted, field.value)}
          id={field.name}
          required={required}
          {...field}
          {...rest}
          {...(transform && {
            value: transform.input(field.value),
            onChange: event => {
              field.onChange(transform.output(event.target.value))
              transform.positioner?.(event, transform.output(event.target.value), transform.input(field.value))
            },
          })}
        />
      )}
      {hasError && (
        <FormHelperText>
          <>{error}</>
        </FormHelperText>
      )}
    </FormControl>
  )
}
